<template>
  <el-dialog
    :visible="true"
    width="1050px"
    :close-on-click-modal="false"
    :before-close="handleClose"
    :fullscreen="true"
  >
    <div slot="title">
      <span v-if="type === 'add'">Új proforma megrendelés</span>
      <span v-if="type === 'update'">Proforma megrendelés módosítása</span>
      <span v-if="type === 'view'">Proforma megrendelés megtekintése</span>
      <el-tag
        v-if="proformCustomerOrder.status && (type === 'update' || type === 'view')"
        size="small"
        :type="getStatus().tag"
        style="margin-left: 5px;"
      >
        {{ getStatus().label }}
      </el-tag>
    </div>
    <el-alert
      v-if="proformCustomerOrder.status === 'done' && type !== 'view'"
      title="Ez a vevői megrendelés már le lett zárva, ezért nem módosítható."
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 30px; margin-top: -20px;"
    />
    <div v-if="type === 'view' && proformCustomerOrder.receipt_number">
      <el-link @click="openReceipt" type="primary" style="margin-bottom: 30px; margin-top: -20px;">
        <i class="el-icon-printer el-icon--right"></i>
        {{ proformCustomerOrder.receipt_number }}
      </el-link>
    </div>
    <div v-loading="loading">
      <el-form
        ref="orderForm"
        label-position="top"
        :model="proformCustomerOrder"
        :rules="rules"
      >
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="supplier"
              label="Szállító"
            >
              <el-select
                v-model="proformCustomerOrder.supplier"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="proformCustomerOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="supplier in suppliers"
                  :key="supplier.id + 'supplier'"
                  :value="supplier"
                  :label="supplier.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="storage"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Szállitó raktár
                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                <el-tooltip
                  class="item"
                  effect="dark"
                  :content="`Ebből a raktárból küldjük vissza az árut. ${$config('STORAGE_TOOLTIP_SUFFIX')}`"
                  placement="right"
                >
                  <i class="el-icon-info"/>
                </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="proformCustomerOrder.storage"
                style="width: 100%;"
                value-key="id"
                :disabled="proformCustomerOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="storage in storages"
                  :key="storage.id + 'storage'"
                  :value="storage"
                  :label="storage.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="customer"
              label="Vevő"
            >
              <el-select
                v-model="proformCustomerOrder.customer"
                filterable
                remote
                :remote-method="searchPartners"
                :loading="partnerLoading"
                :disabled="proformCustomerOrder.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                value-key="id"
                placeholder="Írd be a partner nevét a kereséshez..."
                @change="onCustomerChange"
              >
                <el-option
                  v-for="customer in customers"
                  :key="customer.id + 'customer'"
                  :value="customer"
                  :label="`#${customer.id} ${customer.company.name}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="customer_address"
              label="Vevő szállítási címe"
            >
              <el-select
                v-model="proformCustomerOrder.customer_address"
                v-loading="addressesLoading"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="proformCustomerOrder.status !== 'recorded' || addresses.length === 0 || type === 'view'"
              >
                <el-option
                  v-for="address in addresses"
                  :key="address.id + 'address'"
                  :value="address"
                  :label="`${address.full_address}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="receipt_prefix"
              label="Bizonylat előtag"
            >
              <el-input
                v-model="receiptPrefix"
                disabled
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="order_numbers"
              label="Megrendelésszám"
            >
              <el-input
                v-model="proformCustomerOrder.order_numbers"
                :disabled="proformCustomerOrder.status !== 'recorded' || type === 'view'"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="currency_id"
              label="Deviza"
            >
              <el-select
                v-model="proformCustomerOrder.currency"
                :disabled="proformCustomerOrder.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                value-key="id"
                filterable
              >
                <el-option
                  v-for="currency in currencies"
                  :key="currency.id + 'currency'"
                  :value="currency"
                  :label="currency.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="delivery_date"
              label="Szállítás dátuma"
            >
              <el-date-picker
                v-model="proformCustomerOrder.delivery_date"
                type="date"
                :disabled="proformCustomerOrder.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                :clearable="false"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <product-items
          ref="productItems"
          :currency="proformCustomerOrder.currency && proformCustomerOrder.currency.code"
          :default-items="proformCustomerOrder.order_items"
          price-type="price"
          :custom-prices="customPrices"
          :enabled-elements="enabledElements"
          :storage-id="proformCustomerOrder.storage && proformCustomerOrder.storage.id"
          :enabled-columns="type === 'view' ? ['product', 'productPackType', 'expirationDateSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'supplierSelector'] : ['product', 'productPackType', 'expirationDateSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'actions', 'supplierSelector']"
          @priceChange="onPriceChange"
        />
      </el-form>
      <div class="price-sum">
        <div>Nettó: <strong>{{ proformCustomerOrder.net_price | currency(proformCustomerOrder.currency.code) }}</strong></div>
        <div style="margin-left: 20px;">
          Bruttó: <strong>{{ proformCustomerOrder.gross_price | currency(proformCustomerOrder.currency.code) }}</strong>
        </div>
      </div>
    </div>
    <div
      v-if="type !== 'view'"
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        :disabled="proformCustomerOrder.status !== 'recorded' || loading"
        type="primary"
        :loading="buttonLoading"
        @click="store"
      >
        Mentés
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import ProductItems from '../../utils/ProductItems';

export default {
  components: {
    ProductItems,
  },
  props: ['type', 'proformCustomerOrderId'],
  data() {
    return {
      loading: false,
      addressesLoading: false,
      receiptPrefix: '',
      buttonLoading: false,
      partnerLoading: false,
      cancelButtonLoading: false,
      restoreButtonLoading: false,
      addresses: [],
      customers: [],
      customPrices: [],
      proformCustomerOrder: {
        status: 'recorded',
        net_price: 0,
        gross_price: 0,
        order_items: [],
        storage: {},
        target_storage: {},
        customer: null,
        supplier: {},
        completed_at: null,
        currency: {},
        delivery_date: null,
      },
      rules: {
        customer: [
          {
            required: true,
            message: 'Vevő kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        supplier: [
          {
            required: true,
            message: 'Beszállító kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        storage: [
          {
            required: true,
            message: 'Raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        target_storage: [
          {
            required: true,
            message: 'Raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        completed_at: [
          {
            required: true,
            message: 'Teljesítés megadása kötelező',
            trigger: 'change'
          }
        ],
        currency: [
          {
            required: true,
            message: 'Deviza megadása kötelező',
            trigger: 'change'
          }
        ],
      },
    };
  },
  computed: {
    enabledElements() {
      if(this.type === 'view') {
        return [];
      }
      switch (this.proformCustomerOrder.status) {
        case 'recorded':
          return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'netPrice', 'vat', 'unitPrice', 'expirationDateSelector', 'supplierSelector'];
        case 'sent':
          return [];
        case 'done':
          return [];
        default:
          return [];
      }
    },
    suppliers() {
      return this.$store.getters['ownCompany/ownCompanies'];
    },
    storages() {
      return this.$store.getters['storage/ownStorages'];
    },
    currencies() {
      return this.$store.getters['currency/currencies'];
    },
  },
  async mounted() {
    this.loading = true;
    const requests = [];
    if (!this.currencies || !this.currencies.length) {
      requests.push(this.$store.dispatch('currency/fetchAll'));
    }
    if (!this.storages || !this.storages.length) {
      requests.push(this.$store.dispatch('storage/fetchAll'));
    }
    if (!this.suppliers || !this.suppliers.length) {
      requests.push(this.$store.dispatch('ownCompany/fetchAll'));
    }
    await Promise.all(requests);
    this.receiptPrefix = (await axios.get('api/receipt-prefixes/customerProformOrder')).full_prefix;
    if (this.proformCustomerOrderId) {
      this.proformCustomerOrder = await this.$store.dispatch('proformCustomerOrder/fetch', this.proformCustomerOrderId);
      this.customers.push(this.proformCustomerOrder.customer);
      this.customPrices = await axios.get(`api/customer-price-lists/partner/${this.proformCustomerOrder.customer.id}`);
      this.addresses = this.proformCustomerOrder.customer.company.addresses.filter(a => a.pivot.type === 'delivery');
      if(this.type === 'add') {
        this.proformCustomerOrder.status = 'recorded';
        this.proformCustomerOrder.receipt_number = null;
        this.proformCustomerOrder.customer_order_id = null;
        this.proformCustomerOrder.order_items = this.proformCustomerOrder.order_items.map(item => {
          delete item.id;
          return item;
        });
      }
    } else {
      this.proformCustomerOrder.supplier = this.suppliers[0];
      this.proformCustomerOrder.currency = this.currencies[0];
      this.proformCustomerOrder.storage = this.storages[0];
      this.proformCustomerOrder.delivery_date = moment()
        .add(3, 'days')
        .format('Y-MM-DD');
    }
    this.loading = false;
  },
  methods: {
    async searchPartners(query) {
      this.partnerLoading = true;
      if (!query) {
        this.partners = [];
      } else {
        const queryString = new URLSearchParams({
          name: query,
        });
        this.customers = await this.$store.dispatch('partner/search', '?' + queryString.toString());
      }
      this.partnerLoading = false;
    },
    async onCustomerChange() {
      this.addressesLoading = true;
      this.addresses = await axios.get(`api/addresses/company/${this.proformCustomerOrder.customer.company.id}/delivery`);
      if (this.addresses) {
        this.$set(this.proformCustomerOrder, 'customer_address', this.addresses[0]);
      }
      this.customPrices = await axios.get(`api/customer-price-lists/partner/${this.proformCustomerOrder.customer.id}`);
      this.addressesLoading = false;
    },
    onPriceChange({ netPrice, grossPrice }) {
      this.proformCustomerOrder.net_price = netPrice;
      this.proformCustomerOrder.gross_price = grossPrice;
    },
    handleClose() {
      if(this.type === 'view') {
        return this.$router.replace('/sales/proform-customer-orders');
      }
      this.$confirm('Biztosan elveted a módosításokat?', {
        confirmButtonText: 'Igen',
        type: 'warning',
      })
        .then(() => {
          this.$router.replace('/sales/proform-customer-orders');
        })
        .catch(() => {
        });
    },
    async store() {
      this.$refs.orderForm.validate(async valid => {
        if (valid) {
          this.proformCustomerOrder.order_items = this.$refs['productItems'].getItems();
          if (!this.proformCustomerOrder.order_items.length) {
            return this.$messageWrapper.warning('Minimum 1 termék hozzáadása kötelező.');
          } else if (!this.$refs['productItems'].validateProducts()) {
            return this.$messageWrapper.warning('Termékadatok hibásan vannak kitöltve.');
          }
          this.buttonLoading = true;
          try {
            if (this.type === 'add') {
              await this.$store.dispatch('proformCustomerOrder/create', this.proformCustomerOrder);
            } else if (this.type === 'update') {
              await this.$store.dispatch('proformCustomerOrder/update', this.proformCustomerOrder);
            }
            this.$store.dispatch('proformCustomerOrder/fetchAll');
            this.buttonLoading = false;
            this.$router.replace('/sales/proform-customer-orders');
          } catch (e) {
          }
          this.buttonLoading = false;
        }
      });
    },
    openReceipt() {
      if (this.proformCustomerOrder.receipt_file && this.proformCustomerOrder.receipt_file.path) {
        window.open(this.proformCustomerOrder.receipt_file.path);
      }
    },
    getStatus() {
      switch (this.proformCustomerOrder.status) {
        case 'recorded':
          return {
            label: 'Rögzítve',
            tag: 'info',
          };
        case 'waiting-for-payee':
          return {
            label: 'Fizetésre vár',
            tag: 'primary',
          };
        case 'cancelled':
          return {
            label: 'Érvenytelenítve',
            tag: 'danger',
          };
        case 'partly-done':
          return {
            label: 'Részben teljesítve',
            tag: 'warning',
          };
        case 'done':
          return {
            label: 'Teljesítve',
            tag: 'success',
          };
      }
    },
  },
};
</script>

<style lang="scss">
  .price-sum {
    margin-top: 20px;
    text-align: right;
    font-size: 18px;

    > div {
      display: inline-block;
    }
  }
</style>
